import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { YMaps, useYMaps } from '@pbe/react-yandex-maps'
import { useTranslation } from 'react-i18next'
import { Input } from '@rentflowdev/ui'
import { isPreciseEnough, resolveGeoObject, type GeoObjectLike, type ResolvedAddress } from '@/lib/geo-address'
import { IS_MAPS_CONFIGURED, YANDEX_MAPS_API_KEY } from '@/lib/maps-config'

const YMAPS_QUERY = { apikey: YANDEX_MAPS_API_KEY, lang: 'ru_RU', load: 'package.full' } as const

/** How long the maps script may take to load before the field says it failed. */
const MAPS_LOAD_GRACE_MS = 8000

interface Suggestion {
	displayName: string
	value:       string
}

interface YmapsApi {
	suggest: (query: string, options?: Record<string, unknown>) => Promise<Array<{
		displayName: string
		value:       string
	}>>
	geocode: (query: string) => Promise<{
		geoObjects: { get: (i: number) => GeoObjectLike | undefined }
	}>
}

interface AddressSuggestProps {
	value:        string
	/** Free text — the caller must treat this as "no longer resolved". */
	onChange:     (value: string) => void
	/** Only ever fired for a result precise enough to identify the property. */
	onSelect:     (resolved: ResolvedAddress) => void
	onBlur?:      () => void
	name?:        string
	placeholder?: string
	/** Land plots may stop at street level; everything else needs a building. */
	allowStreetLevel?: boolean
}

type PlainProps = Pick<AddressSuggestProps, 'value' | 'onChange' | 'onBlur' | 'name' | 'placeholder'> & {
	notice?: string
	inputRef?: React.Ref<HTMLInputElement>
}

/**
 * Fallback when the maps API is unavailable. It used to be silent, which made
 * a misconfigured deployment look like a working free-text field; now it says
 * why suggestions are missing.
 */
function PlainInput({ value, onChange, onBlur, name, placeholder, notice, inputRef }: PlainProps) {
	return (
		<div className="flex w-full flex-col gap-1">
			<Input
				ref={inputRef}
				name={name}
				value={value}
				placeholder={placeholder}
				onChange={(e) => onChange(e.target.value)}
				onBlur={onBlur}
				autoComplete="off"
			/>
			{notice && <p className="text-xs text-(--color-muted-foreground)">{notice}</p>}
		</div>
	)
}

interface AddressSuggestInnerProps extends AddressSuggestProps {
	ymaps: YmapsApi
	inputRef?: React.Ref<HTMLInputElement>
}

function AddressSuggestInner({
	value, onChange, onSelect, onBlur, name, placeholder, allowStreetLevel, ymaps, inputRef,
}: AddressSuggestInnerProps) {
	const { t } = useTranslation()
	const [suggestions, setSuggestions] = useState<Suggestion[]>([])
	const [open, setOpen]               = useState(false)
	const [active, setActive]           = useState(-1)
	const [notice, setNotice]           = useState<string | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)
	const debounceRef  = useRef<ReturnType<typeof setTimeout> | null>(null)
	// Holds the last address this component wrote into `value` itself. A
	// one-shot boolean is not enough: handleSelect writes twice — the raw
	// suggestion first, then the geocoder's resolved address — and the second
	// write re-opened the dropdown on top of the address just picked (plus a
	// billed suggest call), where clicking it away wiped the fresh coordinates.
	const committedRef = useRef<string | null>(null)

	const fetchSuggestions = useCallback(async (query: string) => {
		if (query.length < 3) { setSuggestions([]); setOpen(false); return }
		try {
			const results = await ymaps.suggest(query, { results: 7, lang: 'ru_RU' })
			setSuggestions(results.map((r) => ({ displayName: r.displayName, value: r.value })))
			setOpen(results.length > 0)
			setActive(-1)
		} catch {
			setSuggestions([])
			setOpen(false)
		}
	}, [ymaps])

	useEffect(() => {
		if (committedRef.current !== null && value === committedRef.current) return
		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => void fetchSuggestions(value), 300)
		return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
	}, [value, fetchSuggestions])

	useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClick)
		return () => document.removeEventListener('mousedown', handleClick)
	}, [])

	async function handleSelect(s: Suggestion) {
		setOpen(false)
		setNotice(null)
		committedRef.current = s.value
		onChange(s.value)
		try {
			const res      = await ymaps.geocode(s.value)
			const resolved = resolveGeoObject(res.geoObjects.get(0))
			if (!resolved) {
				setNotice(t('properties.form.addressGeocodeFailed'))
				return
			}
			// The geocoder answers nearly anything — a district or a namesake
			// settlement comes back with coordinates too. Only a building-level
			// hit may set them.
			if (!isPreciseEnough(resolved, allowStreetLevel)) {
				setNotice(t('properties.form.addressNeedsHouse'))
				return
			}
			const finalAddress = resolved.address || s.value
			committedRef.current = finalAddress
			onSelect({ ...resolved, address: finalAddress })
		} catch {
			setNotice(t('properties.form.addressGeocodeFailed'))
		}
	}

	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (!open || suggestions.length === 0) return
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setActive((i) => (i + 1) % suggestions.length)
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setActive((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
		} else if (e.key === 'Enter') {
			// Always consume Enter while the list is open. `active` resets to -1
			// after every fetch, so the ordinary "type, then press Enter" path
			// used to fall straight through to the enclosing form's implicit
			// submit — advancing the wizard step and painting "pick an address
			// from the suggestions" under a list that was open all along.
			e.preventDefault()
			void handleSelect(suggestions[active >= 0 ? active : 0])
		} else if (e.key === 'Escape') {
			setOpen(false)
		}
	}

	const listId = name ? `${name}-suggestions` : 'address-suggestions'

	return (
		<div ref={containerRef} className="relative flex w-full flex-col gap-1">
			<Input
				ref={inputRef}
				name={name}
				value={value}
				placeholder={placeholder}
				onChange={(e) => { setNotice(null); onChange(e.target.value) }}
				onFocus={() => { if (suggestions.length > 0) setOpen(true) }}
				onBlur={onBlur}
				onKeyDown={handleKeyDown}
				autoComplete="off"
				role="combobox"
				aria-expanded={open}
				aria-controls={listId}
				aria-autocomplete="list"
				aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
			/>
			{notice && <p className="text-xs text-(--color-destructive)">{notice}</p>}
			{open && suggestions.length > 0 && (
				<ul
					id={listId}
					role="listbox"
					className="absolute top-full z-50 mt-1 w-full rounded-md border border-(--color-border) bg-(--color-popover) py-1 shadow-md"
				>
					{suggestions.map((s, i) => (
						<li
							key={`${s.value}-${i}`}
							id={`${listId}-${i}`}
							role="option"
							aria-selected={i === active}
							className={`cursor-pointer px-3 py-2 text-sm hover:bg-(--color-accent) ${i === active ? 'bg-(--color-accent)' : ''}`}
							onMouseDown={(e) => {
								e.preventDefault()
								void handleSelect(s)
							}}
						>
							{s.displayName}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

function AddressSuggestWithApi(props: AddressSuggestProps & { inputRef?: React.Ref<HTMLInputElement> }) {
	const { t } = useTranslation()
	const ymaps = useYMaps() as unknown as YmapsApi | null
	// `useYMaps` returns null until the script has loaded, so a hard "suggestions
	// unavailable" on first render told every user their deployment was
	// misconfigured for the seconds the download takes — and pushed them into the
	// map picker, whose result this PR does not gate on precision. Give the load
	// a grace period and only then call it failed.
	const [loadTimedOut, setLoadTimedOut] = useState(false)

	useEffect(() => {
		if (ymaps?.suggest) return
		const timer = setTimeout(() => setLoadTimedOut(true), MAPS_LOAD_GRACE_MS)
		return () => clearTimeout(timer)
	}, [ymaps])

	if (!ymaps?.suggest || !ymaps?.geocode) {
		return (
			<PlainInput
				{...props}
				notice={loadTimedOut ? t('properties.form.addressSuggestUnavailable') : undefined}
			/>
		)
	}

	return <AddressSuggestInner {...props} ymaps={ymaps} />
}

function AddressSuggestNoKey(props: AddressSuggestProps & { inputRef?: React.Ref<HTMLInputElement> }) {
	const { t } = useTranslation()
	return <PlainInput {...props} notice={t('properties.form.addressSuggestUnavailable')} />
}

export const AddressSuggest = forwardRef<HTMLInputElement, AddressSuggestProps>(
	(props, ref) => {
		if (!IS_MAPS_CONFIGURED) return <AddressSuggestNoKey {...props} inputRef={ref} />

		return (
			<YMaps query={YMAPS_QUERY}>
				<AddressSuggestWithApi {...props} inputRef={ref} />
			</YMaps>
		)
	},
)
AddressSuggest.displayName = 'AddressSuggest'
